import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Bell, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export const channelKey = (name: string) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

const QK = ["channel_waitlist"];

function useWaitlist() {
  return useQuery({
    queryKey: QK,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { userId: null as string | null, channels: new Set<string>() };
      const { data } = await supabase
        .from("channel_waitlist" as any)
        .select("channel")
        .eq("user_id", user.id);
      return {
        userId: user.id,
        channels: new Set(((data as any[]) || []).map((r) => r.channel as string)),
      };
    },
  });
}

interface Props {
  channel: string;
  className?: string;
  size?: "sm" | "default";
}

export function NotifyMeButton({ channel, className, size = "default" }: Props) {
  const key = channelKey(channel);
  const { data } = useWaitlist();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  if (justAdded) {
    return (
      <p className={cn("flex items-center justify-center gap-1.5 text-sm text-muted-foreground", className)}>
        <Check className="h-4 w-4 text-primary" /> C'est noté, on vous préviendra !
      </p>
    );
  }
  if (data?.channels.has(key)) {
    return (
      <p className={cn("flex items-center justify-center gap-1.5 text-sm text-muted-foreground", className)}>
        <Check className="h-4 w-4 text-primary" /> C'est noté !
      </p>
    );
  }

  const onClick = async () => {
    setBusy(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: "Connexion requise", description: "Connectez-vous pour être prévenu." });
        return;
      }
      const { error } = await supabase
        .from("channel_waitlist" as any)
        .insert({ user_id: user.id, channel: key } as any);
      if (error && error.code !== "23505") throw error;
      qc.setQueryData(QK, (old: any) => ({
        userId: user.id,
        channels: new Set([...(old?.channels ?? []), key]),
      }));
      if (!error) setJustAdded(true);
    } catch {
      toast({ title: "Erreur", description: "Impossible d'enregistrer votre demande.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size={size} className={className} onClick={onClick} disabled={busy}>
      <Bell className="h-4 w-4 mr-2" /> Prévenez-moi
    </Button>
  );
}
